import { FC, useCallback, useEffect, useRef, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { updateFuneralHomeInfo } from '@shared/api/funeralHomeInfo/funeralHomeInfo.controller';
import { FuneralHomeInfo } from '@shared/api/funeralHomeInfo/funeralHomeInfo.interface';
import { Image as ImageType } from '@shared/api/media/media.interface';
import { Button } from '@shared/components/Button';
import Map from '@shared/components/Map';
import { TextField } from '@shared/components/TextField';
import { invalidateQueriesByUrlPart } from '@shared/hooks/recoil-query';
import { Coord } from '@shared/interfaces/map.interface';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';

import AddressSearcher from '../AddressSearcher/AddressSearcher';
import { FuneralHomeAdditionalInfoEditor } from './FuneralHomeAdditionalInfoEditor';
import {
  AdditionalInfoDraft,
  toAdditionalInfoDrafts,
  toAdditionalInfoPayload,
} from './funeralHomeAdditionalInfo.util';

enum FuneralHomeInfoFormEnum {
  Name = 'name',
  PhoneNumber = 'phoneNumber',
  Address = 'address',
  ParkingAddress = 'parkingAddress',
  Coord = 'coord',
}

export interface IFuneralHomeInfoForm {
  [FuneralHomeInfoFormEnum.Name]: string;
  [FuneralHomeInfoFormEnum.PhoneNumber]: string;
  [FuneralHomeInfoFormEnum.Address]: string;
  [FuneralHomeInfoFormEnum.ParkingAddress]: string;
  [FuneralHomeInfoFormEnum.Coord]: { x: string; y: string };
}

interface IProps {
  //* 추가정보 사진이 populate 된 단건도, populate 없는 목록 항목도 받는다
  funeralHomeInfo: FuneralHomeInfo<undefined, ImageType | string>;
}

export const FuneralHomeInfoEditor: FC<IProps> = ({ funeralHomeInfo }: IProps) => {
  const { register, handleSubmit, setValue } = useForm<IFuneralHomeInfoForm>({
    defaultValues: {
      name: funeralHomeInfo?.name,
      phoneNumber: funeralHomeInfo?.phoneNumber,
      address: funeralHomeInfo?.address,
      parkingAddress: funeralHomeInfo?.parkingAddress,
      coord: funeralHomeInfo.coord,
    },
  });

  const queryClient = useQueryClient();
  const notifiacationInstance = useRecoilValue(notificationInstanceAtom);

  const { assetRole } = useAuth();

  //* 추가정보 — 부고 주차안내 뒤에 순서대로 노출되는 안내 블록
  const [additionalInfos, setAdditionalInfos] = useState<AdditionalInfoDraft[]>(() =>
    toAdditionalInfoDrafts(funeralHomeInfo),
  );
  const seededIdRef = useRef<string>(funeralHomeInfo._id);

  useEffect(() => {
    //* 창 포커스 refetch로 props가 새로 와도 편집 중인 배열을 덮어쓰지 않는다.
    //* 다른 장례식장으로 바뀔 때만 다시 시딩한다.
    if (seededIdRef.current === funeralHomeInfo._id) return;
    seededIdRef.current = funeralHomeInfo._id;
    setAdditionalInfos(toAdditionalInfoDrafts(funeralHomeInfo));
  }, [funeralHomeInfo]);

  //* 주소검색으로 새로 고른 주소(고르기 전엔 null → 기존 주소 유지)
  const [selectedAddress, setSelectedAddress] = useState<{
    address: string;
    coord?: Coord;
  } | null>(null);

  const onAddressChange = useCallback(
    (addressItem: { address: string; coord?: Coord }) => {
      //* AddressSearcher는 마운트/검색어 변경 때 빈 주소로 초기화 신호를 보낸다.
      //* 이걸 그대로 반영하면 주소검색을 안 하고 저장만 해도 기존 주소가 지워지므로 무시한다.
      if (!addressItem.address) return;

      setValue(FuneralHomeInfoFormEnum.Address, addressItem.address);
      if (addressItem.coord) setValue(FuneralHomeInfoFormEnum.Coord, addressItem.coord);
      setSelectedAddress(addressItem);
    },
    [setValue],
  );

  const onAddressReset = () => {
    setValue(FuneralHomeInfoFormEnum.Address, funeralHomeInfo.address);
    setValue(FuneralHomeInfoFormEnum.Coord, funeralHomeInfo.coord);
    setSelectedAddress(null);
  };

  //* 저장 버튼을 누르면 실제로 저장될 주소/좌표
  const targetAddress = selectedAddress?.address ?? funeralHomeInfo.address;
  const targetCoord = selectedAddress?.coord ?? funeralHomeInfo.coord;
  const hasCoord = Boolean(targetCoord?.x && targetCoord?.y);

  const onSubmit = async (data: IFuneralHomeInfoForm) => {
    try {
      await updateFuneralHomeInfo(
        {
          _id: funeralHomeInfo._id,
          ...data,
          additionalInfos: toAdditionalInfoPayload(additionalInfos),
        },
        assetRole(),
      );
      //* 목록·단건 둘 다 무효화 (문자열 키는 prefix 매칭이 안 돼서 헬퍼로)
      invalidateQueriesByUrlPart(queryClient, 'funeral-home-info');
      notificateSuccess(notifiacationInstance, '저장완료');
    } catch (error) {
      notificateError(notifiacationInstance, '에러');
      console.error(error);
    }
  };

  return (
    <div className="space-y-3 text-sm">
      {/* Name */}
      <div className="space-y-2">
        <h5 className="font-medium">장례식장 이름</h5>
        <TextField
          type="text"
          autoComplete="off"
          className="w-full"
          placeholder="장례식장 이름"
          {...register(FuneralHomeInfoFormEnum.Name, {
            required: true,
          })}
        ></TextField>
      </div>
      {/* PhoneNumber */}
      <div className="space-y-2">
        <h5 className="font-medium">전화번호</h5>
        <TextField
          type="text"
          autoComplete="off"
          className="w-full"
          placeholder="전화번호"
          {...register(FuneralHomeInfoFormEnum.PhoneNumber)}
        ></TextField>
      </div>
      {/* 저장될 주소 + 지도 */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h5 className="font-medium">{selectedAddress ? '변경될 주소' : '현재주소'}</h5>
          {selectedAddress && (
            <button
              type="button"
              className="text-xs font-medium underline theme-text-8"
              onClick={onAddressReset}
            >
              되돌리기
            </button>
          )}
        </div>
        <p className="pl-2">{targetAddress || '주소 없음'}</p>
        {selectedAddress && (
          <p className="pl-2 text-xs theme-text-8">
            기존 주소 : {funeralHomeInfo.address || '없음'}
          </p>
        )}
        {hasCoord && targetCoord ? (
          <div className="h-[200px] w-full" key={`${targetCoord.x},${targetCoord.y}`}>
            <Map coord={targetCoord} name={funeralHomeInfo.name} />
          </div>
        ) : (
          <p className="pl-2 text-xs theme-text-8">
            저장된 좌표가 없습니다. 아래 주소검색으로 위치를 지정해 주세요.
          </p>
        )}
      </div>
      {/* Address */}
      <div className="space-y-2">
        <h5 className="font-medium">주소검색</h5>
        <AddressSearcher onChange={onAddressChange} />
      </div>
      {/* parkingAddress */}
      <div className="space-y-2">
        <h5 className="font-medium">주차안내</h5>
        <TextField
          type="text"
          autoComplete="off"
          className="w-full"
          placeholder="주차안내"
          {...register(FuneralHomeInfoFormEnum.ParkingAddress)}
        ></TextField>
      </div>
      {/* 추가정보 */}
      <FuneralHomeAdditionalInfoEditor
        items={additionalInfos}
        setItems={setAdditionalInfos}
      />
      {/* Confirm Button */}
      <div>
        <Button
          type="submit"
          onClick={handleSubmit(onSubmit)}
          className="button-rectangle mt-8 text-white theme-bg-main"
        >
          저장
        </Button>
      </div>
    </div>
  );
};
